import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CachedImage from '@components/CachedImage/CachedImage';
import { NonMemberContent } from '@components/Profile/TeamDetailPage/NonMemberContent.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Skeleton } from '@components/shared/Skeleton';
import Tag from '@components/shared/Tags/Tag';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { LocalFile, Team, TeamMemberRole, TeamRole } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { isTeamExpired } from '@utils/teams/paywall';

const planIcon = ({ team }: { team?: Team }) => {
  if (team?.avatarFile)
    return (
      <Box marginRight='xs'>
        <CachedImage
          avatarId={team.avatarFile.id}
          avatarType='team'
          image={team.avatarFile as unknown as LocalFile}
          width={48}
          height={48}
          borderRadius={theme.spacing.xxs}
        />
      </Box>
    );
  if (!team?.avatarFile && team?.personal)
    return (
      <Box
        marginRight='xs'
        width={48}
        height={48}
        backgroundColor='greenSecondaryMild'
        borderRadius='xs'
        alignItems='center'
        justifyContent='center'>
        <Icon variant='l' name='Office' />
      </Box>
    );
  else
    return (
      <Box
        marginRight='xs'
        width={48}
        height={48}
        backgroundColor='black'
        borderRadius='xs'
        alignItems='center'
        justifyContent='center'>
        <Icon name='TT48' width={48} height={48} />
      </Box>
    );
};

const InfoPart = ({
  team,
  fullTeam,
}: {
  team?: Team;
  // TA-10280: `team` (from useWebDrawerNavigator's selectedTeam context)
  // comes from the list query, which only fetches `owner { id }` and
  // never fetches `skills` at all. Reading Owner/Skills off `team` renders
  // them permanently blank (not just while loading). `fullTeam` is the
  // getTeamDetail query result, which has both fields.
  fullTeam?: Team;
}) => {
  const { setSelectedTeam, selectedTeam } = useWebDrawerNavigator();
  const navigation = useAppNavigation();
  const { me } = useMe();
  const { t } = useTranslation('models');

  const capitalizeFirstLetter = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getUserRole = (item: Team): string => {
    if (item?.owner?.id === me?.id) return TeamMemberRole.Owner;
    return item?.currentUserRole ?? '';
  };

  useEffect(() => {
    const unsubscribe = navigation
      .getParent()
      ?.addListener('drawerItemPress', (e) => {
        setSelectedTeam(undefined);
        navigation.reset({ routes: [{ name: 'tabs' }], index: 0 });
        e.preventDefault();
      });

    return unsubscribe;
  }, [navigation]);

  if (!team) {
    // TA-10280: Team Info card skeleton (logo + owner/role/skills lines)
    // while team metadata fetches — matches the real rows below.
    return (
      <Box testID='team-info-card-skeleton'>
        <Box flexDirection='row' marginBottom='l' alignItems='center'>
          <Box marginRight='xs'>
            <Skeleton variant='rect' width={48} height={48} borderRadius='xs' />
          </Box>
          <Skeleton variant='text' width={160} height={22} />
        </Box>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          height={52}
          borderBottomWidth={1}
          borderBottomColor='grey02'>
          <Skeleton variant='text' width={60} />
          <Skeleton variant='text' width={100} />
        </Box>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          height={52}
          borderBottomWidth={1}
          borderBottomColor='grey02'>
          <Skeleton variant='text' width={60} />
          <Skeleton variant='rect' width={70} height={24} borderRadius='l' />
        </Box>
        <Box paddingVertical='m'>
          <Skeleton
            variant='text'
            width={50}
            style={{ marginBottom: theme.spacing.xxs }}
          />
          <Box flexDirection='row' style={{ gap: theme.spacing.xxs }}>
            <Skeleton
              variant='rect'
              width={70}
              height={20}
              borderRadius='xxs'
            />
            <Skeleton
              variant='rect'
              width={90}
              height={20}
              borderRadius='xxs'
            />
          </Box>
        </Box>
      </Box>
    );
  }

  const roleLabel = team ? capitalizeFirstLetter(getUserRole(team)) : '';

  return (
    <>
      <Box flexDirection='row' marginBottom='l' alignItems='center'>
        {planIcon({ team })}
        <Text
          accessibilityLabel='Team Name'
          variant='heading2'
          numberOfLines={1}
          color='textSecondary'
          style={{ maxWidth: 340 }}>
          {team?.name}
        </Text>
        <Box alignItems='center' marginStart='xs' marginTop='xs'>
          {selectedTeam?.id === me?.primaryTeamId && (
            <Icon variant='l' color='textSecondary' name='StarYellow' />
          )}
        </Box>
      </Box>

      {/* Owner row */}
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        height={52}
        borderBottomWidth={1}
        borderBottomColor='grey02'>
        <Text variant='webBodySecondary' color='textSecondary'>
          {t('teams.overview.teamInfo.owner')}
        </Text>
        {!fullTeam ? (
          <Skeleton variant='text' width={100} />
        ) : (
          <Text variant='webBodySecondary' color='textSecondary'>
            {fullTeam.owner
              ? `${fullTeam.owner.firstName ?? ''} ${
                  fullTeam.owner.lastName ?? ''
                }`.trim() || '-'
              : '-'}
          </Text>
        )}
      </Box>

      {/* My Role row */}
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        height={52}
        borderBottomWidth={1}
        borderBottomColor='grey02'>
        <Text flex={1} variant='webBodySecondary' color='textSecondary'>
          {t('teams.details.myRole')}
        </Text>
        {roleLabel ? (
          <Box
            borderRadius='l'
            height={24}
            backgroundColor='grey01'
            paddingHorizontal='xs'
            justifyContent='center'>
            <Text variant='webBodySecondary' color='grey05' mx='xs'>
              {roleLabel}
            </Text>
          </Box>
        ) : (
          <Text variant='body' color='textSecondary'>
            -
          </Text>
        )}
      </Box>

      {/* Skills row */}
      <Box paddingVertical='m'>
        <Text variant='webBodySecondary' color='textSecondary' mb='xxs'>
          {t('teams.details.skills')}
        </Text>
        <Box
          flexDirection='row'
          flexWrap='wrap'
          style={{ gap: theme.spacing.xxs }}>
          {!fullTeam ? (
            <>
              <Skeleton
                variant='rect'
                width={70}
                height={20}
                borderRadius='xxs'
              />
              <Skeleton
                variant='rect'
                width={90}
                height={20}
                borderRadius='xxs'
              />
            </>
          ) : (
            fullTeam.skills?.map((s) => (
              <Tag
                key={s.id}
                variant='status'
                label={s.name}
                labelVariant='labelSmall'
                padding='xxs'
                borderRadius='xxs'
                color='textSecondary'
              />
            ))
          )}
        </Box>
      </Box>
    </>
  );
};

const PerformanceMetric = ({
  percentage,
  label,
  description,
  borderRight,
}: {
  percentage: string;
  label: string;
  description: string;
  borderRight?: boolean;
}) => (
  <Box
    flex={1}
    paddingRight={borderRight ? 'm' : undefined}
    paddingLeft={borderRight ? undefined : 'm'}
    borderRightWidth={borderRight ? 1 : 0}
    borderRightColor='grey02'>
    <Text variant='heading1' color='greenSecondary' mb='xs'>
      {percentage}
    </Text>
    <Text variant='webBodySecondary' color='textSecondary' mb='m'>
      {label}
    </Text>
    <Text variant='webBodyHintSubline' color='grey04'>
      {description}
    </Text>
  </Box>
);

const ExpiredPerformanceMetric = ({
  label,
  description,
  borderRight,
}: {
  label: string;
  description: string;
  borderRight?: boolean;
}) => (
  <Box
    flex={1}
    paddingRight={borderRight ? 'm' : undefined}
    paddingLeft={borderRight ? undefined : 'm'}
    borderRightWidth={borderRight ? 1 : 0}
    borderRightColor='grey02'>
    <Text variant='heading1' color='grey04' mb='xs'>
      —
    </Text>
    <Text variant='webBodySecondary' color='textSecondary' mb='m'>
      {label}
    </Text>
    <Text variant='webBodyHintSubline' color='grey04'>
      {description}
    </Text>
  </Box>
);

// TA-10280: replaces the `?? 0` fallback's hard "0%" flash pre-resolve with
// a placeholder, matching PerformanceMetric's layout.
const PerformanceMetricSkeleton = ({
  borderRight,
}: {
  borderRight?: boolean;
}) => (
  <Box
    flex={1}
    paddingRight={borderRight ? 'm' : undefined}
    paddingLeft={borderRight ? undefined : 'm'}
    borderRightWidth={borderRight ? 1 : 0}
    borderRightColor='grey02'>
    <Skeleton
      variant='text'
      width={50}
      height={32}
      style={{ marginBottom: theme.spacing.xs }}
    />
    <Skeleton
      variant='text'
      width='70%'
      style={{ marginBottom: theme.spacing.m }}
    />
    <Skeleton variant='text' width='90%' />
  </Box>
);

const FeatureItem = ({ label }: { label: string }) => (
  <Box flexDirection='row' alignItems='center' mb='m'>
    <Icon name='Check' color='greenSecondary' variant='s' marginRight='xxs' />
    <Text variant='webBodySecondary' color='textSecondary'>
      {label}
    </Text>
  </Box>
);

const PersonalSubscriptionPanel = ({ team }: { team?: Team }) => {
  const { t } = useTranslation('models');
  const isActive = team?.teamSubscriptionStatus === 'active';
  const [bannerVisible, setBannerVisible] = useState(!isActive);

  const projectsUsed = team?.numberOfProjectsUsed ?? 0;
  const maxProjects = team?.maxNumberOfProjects ?? 3;
  const memberCount = team?.usersCount ?? 1;

  return (
    <Box
      flex={1}
      alignSelf='flex-start'
      borderWidth={1}
      borderColor='grey02'
      borderRadius='xs'
      p='l'>
      {/* Header */}
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        mb='xxs'>
        <Text variant='labelLarge' color='textSecondary'>
          {t('teams.overview.subscription.title')}
        </Text>
        <Box
          borderRadius='xxs'
          marginLeft='xs'
          backgroundColor={isActive ? 'blueMild' : 'greenSecondaryMild'}
          paddingHorizontal='xs'
          paddingVertical='xxs'>
          <Text
            variant='webBodySecondary'
            color={isActive ? 'blue' : 'greenSecondary'}>
            {isActive
              ? t('projects.members.active')
              : t('teams.overview.subscription.freePlan')}
          </Text>
        </Box>
      </Box>

      {/* Plan row */}
      <Box
        mt='xs'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        py='m'
        borderBottomWidth={1}
        borderBottomColor='grey02'>
        <Text variant='webBodySecondary' color='grey04'>
          {t('teams.overview.subscription.plan')}
        </Text>
        <Text variant='webBodySecondary' color='textSecondary'>
          {t('teams.overview.subscription.personalProjectsPlan')}
        </Text>
      </Box>

      {/* Members row */}
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        py='m'
        borderBottomWidth={1}
        borderBottomColor='grey02'>
        <Text variant='webBodySecondary' color='grey04'>
          {t('teams.overview.subscription.members')}
        </Text>
        <Text variant='webBodySecondary' color='textSecondary'>
          {memberCount}
        </Text>
      </Box>

      {/* Projects row */}
      {!isActive && (
        <Box flexDirection='row' justifyContent='space-between' py='m'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('teams.overview.subscription.projects')}
          </Text>
          <Text variant='webBodySecondary' color='yellowVivid'>
            {t('teams.overview.subscription.projectsUsed', {
              used: projectsUsed,
              max: maxProjects === 0 ? '3' : maxProjects,
            })}
          </Text>
        </Box>
      )}

      {isActive && (
        <Box flexDirection='row' justifyContent='space-between' pt='m'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('teams.overview.subscription.renews')}
          </Text>
          <Text variant='webBodySecondary' color='textSecondary'>
            {team?.entitlementExpiration
              ? dayjs(team.entitlementExpiration).format('MMMM DD, YYYY')
              : '—'}
          </Text>
        </Box>
      )}

      {/* Free plan notice banner */}
      {bannerVisible && (
        <Box
          mt='s'
          borderWidth={1}
          borderColor='yellowVivid'
          borderRadius='xs'
          backgroundColor='yellowVividMild'
          p='m'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'>
          <Box flexDirection='row' alignItems='center' flex={1} marginRight='s'>
            <Icon
              name='AlertTriangle'
              color='yellowVivid'
              variant='s'
              marginRight='xs'
            />
            <Text variant='webBodySecondary' color='textPrimary' flex={1}>
              {t('teams.overview.subscription.freePlanNoticePrefix')}
              <Text variant='webSmall' color='textPrimary'>
                {t('teams.overview.subscription.freePlan')}
              </Text>
              {t('teams.overview.subscription.freePlanNoticeMid')}
              <Text variant='webSmall' color='textPrimary'>
                {maxProjects}
                {t('teams.overview.subscription.freePlanNoticeSuffix')}
              </Text>
            </Text>
          </Box>
          <Icon
            name='X'
            variant='s'
            color='black'
            onPress={() => setBannerVisible(false)}
          />
        </Box>
      )}
    </Box>
  );
};

export const DetailsTab = () => {
  const { t } = useTranslation('models');
  const { selectedTeam: team } = useWebDrawerNavigator();

  // getPublicTeam, not getTeam: a user who arrived from a team invite link is
  // not a member yet, and getTeam answers "The requested team could not be
  // found" for them. `fullTeam` is the Team branch of the union, so it stays
  // undefined for non-members — who are sent to NonMemberContent by the
  // isNotTeamMember guard below rather than shown the cards.
  const {
    fullTeam: teamDetail,
    publicTeam,
    hasPendingMemberAdditionRequest,
    loading: teamDetailLoading,
    error: teamDetailError,
  } = useTeamDetailFromQuery({
    teamUUId: team?.uuid ?? '',
    fetchPolicy: 'cache-first',
    // Matches TeamDetailPage, the screen this tab renders inside, so both
    // read the same cache entry.
    includePending: true,
  });

  // Matches TeamDetailPage's own membership rule rather than just testing for
  // the PublicTeam branch. getPublicTeam can answer with the Team branch for a
  // viewer who is not a member yet — that was TA-10772's regression — and in
  // that case `currentUserRole` is the signal. Gating on the branch alone left
  // this tab rendering member cards over data it does not have while the page
  // around it had already decided the viewer is a non-member.
  // `teamDetailLoading` keeps the join screen from flashing before the query
  // settles, when currentUserRole is undefined only because nothing arrived.
  // `== null`, not `=== undefined`: Team.currentUserRole is Maybe<TeamRole>,
  // so a non-member Team response can carry an explicit null that a strict
  // undefined check would read as "has a role".
  //
  // `!teamDetailError` keeps membership unknown on a failed read — an offline
  // blip settles loading to false with no team, which would otherwise look
  // exactly like a non-member.
  const isNotTeamMember =
    !teamDetailLoading &&
    !teamDetailError &&
    (!!publicTeam || (!!teamDetail && teamDetail.currentUserRole == null));

  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setIsTeamPersonal,
    setTeamValues,
    setUpgradeConfirmModalOpen,
  } = useWebDrawer();

  // TA-10280: `team` (selectedTeam context) comes from the list query,
  // which has no `subscriptionDetails` at all — every field below would
  // read as undefined/false off it, showing the wrong real content (e.g.
  // the upsell block for an actively-subscribed team) instead of holding
  // for the skeleton. Prefer the full getTeam result once it resolves.
  const subscriptionSource = teamDetail ?? team;

  const isOnTrial =
    subscriptionSource?.subscriptionDetails?.subscriptionStatus ===
      'trialing' ||
    subscriptionSource?.subscriptionDetails?.isSubscriptionOnTrial === true;

  const isActive =
    subscriptionSource?.subscriptionDetails?.isSubscriptionActive === true ||
    subscriptionSource?.subscriptionDetails?.subscriptionStatus === 'active';

  const isExpired = isTeamExpired(subscriptionSource);

  const trialEndsAt = subscriptionSource?.subscriptionDetails?.trialEndsAt;
  const trialEndFormatted = trialEndsAt
    ? dayjs(trialEndsAt).format('MMMM D, YYYY')
    : null;

  const expirationDate =
    subscriptionSource?.subscriptionDetails?.expirationDate;
  const renewsFormatted = expirationDate
    ? dayjs(expirationDate).format('MMMM D, YYYY')
    : null;

  const planName =
    subscriptionSource?.subscriptionDetails?.subscriptionPlanName ?? '';
  const seatCount =
    subscriptionSource?.subscriptionDetails?.currentSeatCount ?? 0;

  const goToUpgradeSubscription = () => {
    setIsTaskWebPanelOpen(false);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setTeamValues(team);
    setUpgradeConfirmModalOpen(true);
  };

  const featureItems = [
    t('teams.overview.subscription.unlimitedProjects'),
    t('teams.overview.subscription.sharedStorage'),
    t('teams.overview.subscription.createMultipleTasks'),
    t('teams.overview.subscription.globalActivityLog'),
    t('teams.overview.subscription.teamRoles'),
    t('teams.overview.subscription.projectTemplates'),
  ];

  const renderPerformanceContent = () => {
    if (isExpired) {
      return (
        <>
          <ExpiredPerformanceMetric
            label={t('teams.overview.performance.onTimeDelivery')}
            description={t('teams.overview.performance.onTimeDeliveryDesc')}
            borderRight
          />
          <ExpiredPerformanceMetric
            label={t('teams.overview.performance.highPriorityRate')}
            description={t('teams.overview.performance.highPriorityRateDesc')}
          />
        </>
      );
    }
    if (!teamDetail) {
      return (
        <>
          <PerformanceMetricSkeleton borderRight />
          <PerformanceMetricSkeleton />
        </>
      );
    }
    return (
      <>
        <PerformanceMetric
          percentage={`${teamDetail.onTimeDeliveryRate ?? 0}%`}
          label={t('teams.overview.performance.onTimeDelivery')}
          description={t('teams.overview.performance.onTimeDeliveryDesc')}
          borderRight
        />
        <PerformanceMetric
          percentage={`${teamDetail.highPriorityCompletionRate ?? 0}%`}
          label={t('teams.overview.performance.highPriorityRate')}
          description={t('teams.overview.performance.highPriorityRateDesc')}
        />
      </>
    );
  };

  const renderSubscriptionBody = () => {
    if (!teamDetail) {
      // TA-10280: Subscription card skeleton (plan name/status/trial date
      // lines) while subscription data loads separately from the Team
      // Info card above.
      return (
        <Box testID='subscription-card-skeleton' mt='xs'>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              py='m'
              borderBottomWidth={index < 2 ? 1 : 0}
              borderBottomColor='grey02'>
              <Skeleton variant='text' width={70} />
              <Skeleton variant='text' width={90} />
            </Box>
          ))}
        </Box>
      );
    }

    if (isActive) {
      return (
        <>
          {/* Plan row */}
          <Box
            mt='xs'
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            py='m'
            borderBottomWidth={1}
            borderBottomColor='grey02'>
            <Text variant='webBodySecondary' color='grey04'>
              {t('teams.overview.subscription.plan')}
            </Text>
            <Text variant='webBodySecondary' color='textSecondary'>
              {planName}
            </Text>
          </Box>

          {/* Members row */}
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            py='m'
            borderBottomWidth={1}
            borderBottomColor='grey02'>
            <Text variant='webBodySecondary' color='grey04'>
              {t('teams.overview.subscription.members')}
            </Text>
            <Text variant='webBodySecondary' color='textSecondary'>
              {seatCount}
            </Text>
          </Box>

          {/* Renews row */}
          <Box
            flexDirection='row'
            justifyContent='space-between'
            height={36}
            pt='m'>
            <Text variant='webBodySecondary' color='grey04'>
              {t('teams.overview.subscription.renews')}
            </Text>
            <Text variant='webBodySecondary' color='textSecondary'>
              {renewsFormatted ?? '-'}
            </Text>
          </Box>
        </>
      );
    }

    return (
      <>
        {isExpired && (
          <Box my='l' backgroundColor='yellowVividMild' borderRadius='xs' p='m'>
            <Text variant='webBodySecondary' color='textPrimary'>
              {isExpired
                ? t('teams.overview.subscription.teamPlanEndedOn')
                : t('teams.overview.subscription.trialEndsOnPlain')}
              <Text variant='webSmall' color='textPrimary'>
                {isExpired ? renewsFormatted : trialEndFormatted}
              </Text>
            </Text>
            <Text variant='webBodyHintSubline' color='grey04' mt='xs'>
              {isExpired
                ? t('teams.overview.subscription.teamFeaturesSuspended')
                : t('teams.overview.subscription.noPaymentUntilTrialEnds')}
            </Text>
          </Box>
        )}

        {/* What you'll get */}
        <Text
          variant='webBodySecondary'
          color='textSecondary'
          mt={isOnTrial || isExpired ? 'xxs' : 'm'}
          mb='m'>
          {isExpired
            ? t('teams.overview.subscription.whatYoullGetBack')
            : t('teams.overview.subscription.whatYoullGet')}
        </Text>

        {featureItems.map((item, index) => (
          <FeatureItem key={index} label={item} />
        ))}

        {/* Activate Plan button */}
        {(team?.currentUserRole === TeamRole.Admin ||
          team?.currentUserRole === TeamRole.Owner) && (
          <Box mt='l' alignItems='center'>
            <Button
              height={56}
              variant='primary'
              fullWidth
              isSmall
              onPress={() => {
                goToUpgradeSubscription();
              }}
              accessibilityLabel={
                isExpired
                  ? t('teams.invoiceTab.expiredBanner.renewPlan')
                  : t('teams.overview.subscription.activatePlan')
              }>
              {isExpired
                ? t('teams.invoiceTab.expiredBanner.renewPlan')
                : t('teams.overview.subscription.activatePlan')}
            </Button>
            <Text variant='webBodySecondary' color='grey04' mt='xs'>
              {isExpired
                ? t('teams.overview.subscription.restoreFullAccess')
                : t('teams.overview.subscription.trialEndsDate', {
                    date: trialEndFormatted,
                  })}
            </Text>
          </Box>
        )}
      </>
    );
  };

  // A non-member (e.g. arrived from a team invite link) has none of the data
  // the cards below read, so show the join-request screen rather than
  // skeletons that never resolve. See the isNotTeamMember note above for why
  // this is not just a PublicTeam-branch check.
  if (isNotTeamMember) {
    return (
      <NonMemberContent
        teamUUId={publicTeam?.uuid ?? team?.uuid ?? ''}
        teamId={team?.id}
        hasPendingRequest={hasPendingMemberAdditionRequest}
      />
    );
  }

  return (
    <Box flexDirection='row' mx='m' gap='m'>
      {/* Left column: Team Info + Team Performance stacked */}
      <Box flex={1} gap='m'>
        {/* Team Info panel */}
        <Box borderRadius='xs' borderWidth={1} borderColor='grey02' p='l'>
          <Text variant='labelLarge' mb='m'>
            {t('teams.overview.teamInfo.title')}
          </Text>
          <InfoPart team={team} fullTeam={teamDetail} />
        </Box>

        {/* Team Performance panel */}
        <Box borderRadius='xs' borderWidth={1} borderColor='grey02' p='l'>
          <Box mb={isExpired ? 'xxs' : 'l'}>
            <Text variant='labelLarge' color='textSecondary'>
              {t('teams.overview.performance.title')}
              {`· ${t('teams.overview.performance.period')}`}
            </Text>
            {isExpired && (
              <Text variant='webBodyHintSubline' color='grey04' mt='xxs'>
                {t('teams.overview.performance.unavailable')}
              </Text>
            )}
          </Box>
          <Box flexDirection='row' mt='l'>
            {renderPerformanceContent()}
          </Box>
        </Box>
      </Box>

      {/* Right column: Subscription panel */}
      {team?.personal !== true ? (
        <Box
          flex={1}
          alignSelf='flex-start'
          borderWidth={1}
          borderColor='grey02'
          borderRadius='xs'
          p='l'>
          {/* Subscription header */}
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            mb='xxs'>
            <Text variant='labelLarge' color='textSecondary'>
              {t('teams.overview.subscription.title')}
            </Text>
            {isActive && !isOnTrial && (
              <Box
                borderRadius='xxs'
                marginLeft='xs'
                backgroundColor='blueMild'
                paddingHorizontal='xs'
                paddingVertical='xxs'>
                <Text variant='webBodySecondary' color='blue'>
                  {t('teams.overview.subscription.active')}
                </Text>
              </Box>
            )}
            {(isOnTrial || isExpired) && (
              <Box
                borderRadius='xxs'
                marginLeft='xs'
                backgroundColor='greenSecondaryMild'
                paddingHorizontal='xs'
                paddingVertical='xxs'>
                <Text variant='webBodySecondary' color='greenSecondary'>
                  {t('teams.overview.subscription.freeTrial')}
                </Text>
              </Box>
            )}
          </Box>

          {renderSubscriptionBody()}
        </Box>
      ) : (
        <PersonalSubscriptionPanel team={team} />
      )}
    </Box>
  );
};
